/**
 * RoleGuard — route-level role check (legacy) + permission key check.
 *
 * Kiểm tra cả `allowedRoles` (role string) và `requiredPermissions`
 * (permission keys). Dùng `requiredPermissions` cho hệ thống mới, giữ
 * `allowedRoles` tương thích ngược. Cả 2 đều optional, nhưng phải pass
 * ít nhất 1 nếu được cung cấp.
 *
 * @deprecated Dùng `PrivateRoute` với `requiredPermissions` thay thế.
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Result, Button } from 'antd';
import { useAuth, type UserRole } from '@/hooks/useAuth';
import { useRbac } from '@/hooks/useRbac';
import { ROUTES } from '@/constants/routes';

interface RoleGuardProps {
  /** Role string check (legacy). */
  allowedRoles?: UserRole[];
  /** Permission key check (ưu tiên). */
  requiredPermissions?: string[];
  children: ReactNode;
  /** Show 403 page thay vì redirect (default redirect). */
  showForbidden?: boolean;
}

export function RoleGuard({
  allowedRoles,
  requiredPermissions,
  children,
  showForbidden = true,
}: RoleGuardProps) {
  const { user, hasRole } = useAuth();
  const { hasAny } = useRbac();

  if (!user) return null;

  let rolePass = true;
  if (allowedRoles && allowedRoles.length > 0) {
    rolePass = hasRole(allowedRoles);
  }

  let permPass = true;
  if (requiredPermissions && requiredPermissions.length > 0) {
    permPass = hasAny(requiredPermissions);
  }

  if (rolePass && permPass) {
    return <>{children}</>;
  }

  if (showForbidden) {
    const reasons: string[] = [];
    if (allowedRoles?.length) reasons.push(`vai trò: ${allowedRoles.join(', ')}`);
    if (requiredPermissions?.length) reasons.push(`quyền: ${requiredPermissions.join(', ')}`);
    return (
      <Result
        status="403"
        title="Không có quyền"
        subTitle={`Bạn không có quyền truy cập trang này. Yêu cầu: ${reasons.join('; ')}.`}
        extra={
          <Button type="primary" onClick={() => window.history.back()}>
            Quay lại
          </Button>
        }
      />
    );
  }

  return <Navigate to={ROUTES.dashboard} replace />;
}
